import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { css, keyframes } from "@nju/css";
import { IInfoResponse } from "@nju/data-access-portal-user";
import { salesRoutes } from "@nju/routes";
import { Box, CircleLoader, GeneralErrorNotification } from "@nju/ui";
import { BlockContent } from "@nju/ui";
import { clientProviderMachine } from "./clientProvider.machine";

export const ClientContext =
  // eslint-disable-next-line unicorn/no-null
  React.createContext<IInfoResponse | null>(null);

export function useClient() {
  const value = React.useContext(ClientContext);

  if (value === null) {
    throw new Error("`Missing ClientContext` in tree.");
  }

  return value;
}

export function ClientProvider({ children }: React.PropsWithChildren<unknown>) {
  const router = useRouter();
  const [current] = useMachine(clientProviderMachine);

  const { context } = current;
  const { clientData } = context;
  const isLoading = current.matches("loading");
  const isError = current.matches("error");

  if (isError) {
    return (
      <BlockContent
        border="none"
        grid="narrow"
        rounded="primary"
        spacing="l"
        tone="none"
      >
        <GeneralErrorNotification
          callback={() => {
            return router.push(salesRoutes.panel.get());
          }}
        />
      </BlockContent>
    );
  }

  return (
    <>
      {isLoading && (
        <Box className={loaderWrapper()}>
          <CircleLoader />
        </Box>
      )}
      {!isLoading && (
        <ClientContext.Provider value={clientData}>
          {children}
        </ClientContext.Provider>
      )}
    </>
  );
}

const show = keyframes({
  "0%": { opacity: 0 },
  "1%": { opacity: 0 },
  "100%": { opacity: 1 },
});

const loaderWrapper = css({
  opacity: 0,
  display: "flex",
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
  animationName: `${show}`,
  animationDelay: "0.3s",
  animationDuration: "1s",
  animationFillMode: "forwards",
});
